import { getRoleChipClass, getRoleFilterChipClass } from '../features/filters/index.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { buildTeamLogoHtml } from '../utils/teamLogos.js';
import { formatFvmValue } from '../utils/formatFvm.js';
import { playerToKey } from '../utils/playerKey.js';
import { getPlayerHistory } from './historicalStats.js';
import { buildHistoryTableHTML, buildHistoryCompareTableHTML } from './historyTable.js';
import {
  collectStudioTeams,
  countStudioFilterFacets,
  filterStudioEntries,
  filterStudioDraftEntries,
  groupByBand,
  groupSlotItemsByMantraRole,
  listStudioDraftEntries,
  MANTRA_ROLES,
  UNASSIGNED_STUDIO_SLOT_KEY,
} from './studioCalc.js';
import { BANDS, normalizeBand } from './studioLabels.js';
import {
  applyLabelChipColor,
  renderLabelColorPickerHtml,
  syncLabelColorPickerSelection,
  DEFAULT_STUDIO_LABEL_COLOR,
  normalizeLabelColor,
} from './studioLabelColors.js';
import { normalizeLabelName } from '../services/db/studioLabels.pocketbase.js';
import {
  formatPriceBandLabel,
  findPriceBand,
  renderPriceBandPickerHtml,
} from './studioPriceBands.js';
import {
  ICON_HISTORY,
  ICON_COMPARE,
  ICON_REMOVE,
  ICON_SAVE_CHECK,
  ICON_SLOT,
  ICON_TAG,
  renderUndoExitButton,
} from '../ui/action-buttons.js';

/** Panel staccati dal details mentre sono aperti (position:fixed sul viewport). */
const pinnedPopoverPanels = new WeakMap();

const STUDIO_POPOVER_PANEL_SEL =
  ':scope > .studio-row-price-panel, :scope > .studio-row-meta-panel, :scope > .studio-row-tags-panel, :scope > .studio-row-more-panel';

/** Contenitori con overflow/contain che tagliano i panel absolute dei popover riga. */
const STUDIO_POPOVER_CLIP_HOST_SEL =
  '.players-table-container, .players-table, .pc-actions, .player-card, .studio-slot, .studio-list-scroll, #studio-view, .studio-card';

export const studioViewMethods = {
  _resolveStudioPlayer(playerKey) {
    return this.state.allPlayers.find((p) => playerToKey(p) === playerKey);
  },

  _syncCompareToggleStates() {
    const slots = this.state.studioCompareSlots || [];
    const open = Boolean(this.state.studioCompareModalOpen);
    document.querySelectorAll('.studio-row-compare-toggle').forEach((btn) => {
      const host = btn.closest('[data-player-key]');
      const key = host?.dataset?.playerKey;
      if (!key) return;
      const on = open && slots.includes(key);
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  },

  _renderStudioLabelChipsHtml(labels = []) {
    const labelList = Array.isArray(labels) ? labels : [];
    const tagOptions = this.getStudioLabelOptions?.(labelList)?.custom || [];
    const onSet = new Set(labelList.map((l) => normalizeLabelName(l)));
    if (!tagOptions.length) {
      return '<p class="studio-row-tags-empty">Nessun tag in catalogo</p>';
    }
    return tagOptions
      .map((l) => {
        const on = onSet.has(normalizeLabelName(l));
        const chipColor = this.getStudioLabelColor?.(l) || DEFAULT_STUDIO_LABEL_COLOR;
        return `<button type="button" class="tag-filter studio-label-chip${on ? ' active studio-label-chip--on' : ''}" data-label="${escapeHtml(l)}" aria-pressed="${on ? 'true' : 'false'}" data-label-color="${chipColor}" style="--chip-color: ${chipColor}">${escapeHtml(l)}</button>`;
      })
      .join('');
  },

  _labelsForStudioRow(playerKey) {
    if (this.state.studioTargets?.has(playerKey)) {
      return this.state.studioTargets.get(playerKey)?.labels || [];
    }
    const draft = this.getStudioDraft?.(playerKey);
    return draft?.labels || [];
  },

  _refreshStudioRowTagsContent(tagsPop, labels) {
    const chipsEl = tagsPop.querySelector('.studio-row-tags-chips');
    if (!chipsEl) return;
    chipsEl.innerHTML = this._renderStudioLabelChipsHtml(labels);
    const toggle = tagsPop.querySelector('.studio-row-tags-toggle');
    toggle?.classList.toggle('has-tags', (labels || []).length > 0);
  },

  /** Aggiorna solo i colori sui chip già in pagina (senza smontare i popover). */
  _patchStudioLabelColorsInDom() {
    document
      .querySelectorAll('.studio-label-chip[data-label], .tag-filter.studio-label-filter[data-label]')
      .forEach((chip) => {
        const label = chip.getAttribute('data-label');
        if (!label) return;
        applyLabelChipColor(chip, this.getStudioLabelColor?.(label));
      });
    document.querySelectorAll('.studio-labels-modal-row[data-label]').forEach((row) => {
      const label = row.getAttribute('data-label');
      const picker = row.querySelector('.studio-label-color-picker');
      if (!label || !picker) return;
      syncLabelColorPickerSelection(picker, this.getStudioLabelColor?.(label));
    });
  },

  /** Aggiorna toolbar, popover tag e chip lista dopo cambi al catalogo/colori. */
  refreshStudioLabelCatalogUi({ rebuildQuickActions = false } = {}) {
    this.renderStudioToolbar?.();
    document.querySelectorAll('.studio-row-tags[data-player-key]').forEach((tagsPop) => {
      const playerKey = tagsPop.dataset.playerKey;
      if (!playerKey) return;
      this._refreshStudioRowTagsContent(tagsPop, this._labelsForStudioRow(playerKey));
    });
    document.querySelectorAll('.studio-card[data-player-key]').forEach((card) => {
      const playerKey = card.dataset.playerKey;
      if (!playerKey) return;
      this._syncStudioCardLabelsEl?.(card, this._labelsForStudioRow(playerKey));
    });
    this._patchStudioLabelColorsInDom?.();
    if (rebuildQuickActions) {
      this.syncStudioButtonsInTable?.();
    }
  },

  renderStudioView() {
    const listEl = document.getElementById('studio-list');
    if (!listEl) return;
    // La lista viene ricostruita: chiudi e stacca i pannelli flottanti (tag/prezzo/note)
    // altrimenti restano aperti sul body senza un modo per chiuderli.
    this._closeOtherStudioRowPopovers?.(null);
    this._unpinAllStudioPopoverPanels?.();
    this.renderStudioToolbar();

    const targets = Array.from(this.state.studioTargets.entries());
    const draftEntries = listStudioDraftEntries(this.state.studioDrafts, this.state.studioTargets);
    const resolve = (key) => this._resolveStudioPlayer(key);
    const filters = {
      role: this.state.studioRoleFilter || null,
      label: this.state.studioLabelFilter || null,
      team: this.state.studioTeamFilter || null,
      band: this.state.studioBandFilter || null,
    };
    const filtered = filterStudioEntries(targets, filters, resolve);
    const filteredDrafts = filterStudioDraftEntries(draftEntries, filters, resolve);
    const bandFilter = filters.band;
    const showNumberedSlots = !bandFilter || bandFilter !== UNASSIGNED_STUDIO_SLOT_KEY;
    const showUnslotted = !bandFilter || bandFilter === UNASSIGNED_STUDIO_SLOT_KEY;
    const slotGroups = showNumberedSlots ? groupByBand(filtered) : [];
    const hasListContent = (showUnslotted && filteredDrafts.length > 0) || slotGroups.length > 0;
    const isEmpty = targets.length === 0 && draftEntries.length === 0;

    listEl.innerHTML = '';
    if (isEmpty) {
      listEl.innerHTML = `
        <div class="studio-empty" data-testid="studio-empty">
          <p class="studio-empty-title">Nessun giocatore in Studio</p>
          <p class="studio-empty-hint">Nella lista Svincolati, usa i controlli rapidi per aggiungere tag o prezzo; assegna uno slot per promuovere il giocatore a target.</p>
          <button type="button" class="studio-back-btn studio-empty-back" id="studio-empty-back">Vai alla lista completa</button>
        </div>`;
      listEl.querySelector('#studio-empty-back')?.addEventListener('click', () => this.switchToStudioTab('lista'));
    } else {
    const hasFilters = Boolean(filters.role || filters.label || filters.team || filters.band);
    if (hasFilters && !hasListContent) {
      listEl.innerHTML = `
        <div class="studio-empty" data-testid="studio-empty-filter">
          <p class="studio-empty-title">Nessun target con questi filtri</p>
          <button type="button" class="studio-card-btn studio-card-btn--ghost" id="studio-empty-clear-filters">Azzera filtri</button>
        </div>`;
      listEl.querySelector('#studio-empty-clear-filters')?.addEventListener('click', () => this.clearStudioFilters());
    } else {

    if (showUnslotted && filteredDrafts.length > 0) {
      this._appendStudioSlotSection(listEl, {
        bandKey: UNASSIGNED_STUDIO_SLOT_KEY,
        title: 'Senza slot',
        badgeText: '·',
        items: filteredDrafts,
        resolve,
        testId: 'studio-unslotted',
      });
    }

    for (const group of slotGroups) {
      this._appendStudioSlotSection(listEl, {
        bandKey: group.key,
        title: group.label,
        badgeText: group.key,
        items: group.items,
        resolve,
      });
    }
    }
    }
  },

  _appendStudioSlotSection(listEl, { bandKey, title, badgeText, items, resolve, testId = '' }) {
    const section = document.createElement('details');
    section.className = `studio-slot studio-slot--${bandKey}`;
    section.dataset.band = bandKey;
    if (testId) section.setAttribute('data-testid', testId);

    const header = document.createElement('summary');
    header.className = `studio-band-header studio-band-header--${bandKey}`;
    header.innerHTML = `
      <span class="studio-band-badge" aria-hidden="true">${escapeHtml(badgeText)}</span>
      <span class="studio-band-title">${escapeHtml(title)}</span>
      <span class="studio-band-count">${items.length}</span>
      <span class="studio-band-chevron" aria-hidden="true"></span>
    `;
    section.appendChild(header);

    const body = document.createElement('div');
    body.className = 'studio-slot-body';

    const roleGroups = groupSlotItemsByMantraRole(items, resolve);
    for (const roleGroup of roleGroups) {
      const subset = document.createElement('div');
      subset.className = 'studio-role-subset';
      subset.dataset.role = roleGroup.key;
      subset.setAttribute('aria-label', `${title} · ${roleGroup.label}`);

      const roleHeader = document.createElement('div');
      roleHeader.className = 'studio-role-subset-header';
      roleHeader.innerHTML = `
        <span class="studio-role-subset-label">${escapeHtml(roleGroup.label)}</span>
        <span class="studio-role-subset-count">${roleGroup.items.length}</span>
      `;
      subset.appendChild(roleHeader);

      const roleList = document.createElement('div');
      roleList.className = 'studio-role-subset-list studio-slot-list';
      roleList.dataset.band = bandKey;
      roleList.dataset.role = roleGroup.key;
      for (const [playerKey, target] of roleGroup.items) {
        const player = resolve(playerKey);
        roleList.appendChild(this._buildStudioCard(playerKey, target, player, bandKey));
      }
      subset.appendChild(roleList);
      body.appendChild(subset);
    }

    section.appendChild(body);
    listEl.appendChild(section);
  },

  renderStudioToolbar() {
    const filtersEl = document.getElementById('studio-toolbar-filters');
    if (!filtersEl) return;

    const entries = Array.from(this.state.studioTargets.entries());
    const draftEntries = listStudioDraftEntries(this.state.studioDrafts, this.state.studioTargets);
    const resolve = (key) => this._resolveStudioPlayer(key);
    const teams = collectStudioTeams([...entries, ...draftEntries], resolve);
    const labelsInUse = new Set();
    for (const [, t] of entries) {
      for (const l of t.labels || []) labelsInUse.add(l);
    }
    for (const [, t] of draftEntries) {
      for (const l of t.labels || []) labelsInUse.add(l);
    }
    const catalogCustom = this.getStudioLabelOptions
      ? this.getStudioLabelOptions([]).custom
      : (this.state.studioUserLabels || []);
    const labelSeen = new Set();
    const labelOptions = [];
    for (const l of [...catalogCustom, ...labelsInUse]) {
      if (labelSeen.has(l)) continue;
      labelSeen.add(l);
      labelOptions.push(l);
    }
    labelOptions.sort((a, b) => a.localeCompare(b, 'it'));

    const role = this.state.studioRoleFilter || '';
    const label = this.state.studioLabelFilter || '';
    const team = this.state.studioTeamFilter || '';
    const band = this.state.studioBandFilter || '';
    const hasFilters = Boolean(role || label || team || band);
    const counts = countStudioFilterFacets(entries, resolve, draftEntries);

    const roleChips = MANTRA_ROLES.map((r) => {
      const on = role === r;
      const n = counts.roles[r] || 0;
      return `<button type="button" class="${getRoleFilterChipClass(r)}${on ? ' active' : ''}${n === 0 ? ' is-empty' : ''}" data-role="${r}" aria-pressed="${on ? 'true' : 'false'}">${r} <span class="studio-filter-count">(${n})</span></button>`;
    }).join('');

    const slotChips = BANDS.map(({ key }) => {
      const on = band === key;
      const n = counts.bands[key] || 0;
      return `<button type="button" class="tag-filter studio-slot-filter${on ? ' active' : ''}${n === 0 ? ' is-empty' : ''}" data-band="${key}" aria-pressed="${on ? 'true' : 'false'}" aria-label="Slot ${key}">${key} <span class="studio-filter-count">(${n})</span></button>`;
    }).join('');

    const unslottedCount = counts.unslotted || 0;
    const unslottedOn = band === UNASSIGNED_STUDIO_SLOT_KEY;
    const unslottedChip = `<button type="button" class="tag-filter studio-slot-filter studio-slot-filter--unslotted${unslottedOn ? ' active' : ''}${unslottedCount === 0 ? ' is-empty' : ''}" data-band="${UNASSIGNED_STUDIO_SLOT_KEY}" aria-pressed="${unslottedOn ? 'true' : 'false'}" aria-label="Senza slot">Senza slot <span class="studio-filter-count">(${unslottedCount})</span></button>`;

    const labelChips = labelOptions.map((l) => {
      const on = label === l;
      const n = counts.labels[l] || 0;
      const chipColor = this.getStudioLabelColor?.(l) || DEFAULT_STUDIO_LABEL_COLOR;
      return `<button type="button" class="tag-filter studio-label-filter${on ? ' active' : ''}" data-label="${escapeHtml(l)}" aria-pressed="${on ? 'true' : 'false'}" data-label-color="${chipColor}" style="--chip-color: ${chipColor}"><span class="studio-label-filter-dot" aria-hidden="true"></span>${escapeHtml(l)} <span class="studio-filter-count">(${n})</span></button>`;
    }).join('');

    const teamChips = teams.map((t) => {
      const on = team === t;
      const n = counts.teams[t] || 0;
      const teamEsc = escapeHtml(t);
      const label = `${t} (${n})`;
      return `<button type="button" class="tag-filter studio-team-filter${on ? ' active' : ''}${n === 0 ? ' is-empty' : ''}" data-team="${teamEsc}" aria-pressed="${on ? 'true' : 'false'}" aria-label="${escapeHtml(label)}" title="${teamEsc}">${buildTeamLogoHtml(t, { size: 20, className: 'team-logo team-logo--filter' })}<span class="studio-filter-count" aria-hidden="true">(${n})</span></button>`;
    }).join('');

    filtersEl.innerHTML = `
      <div class="filters-panel studio-filters-panel">
        <div class="studio-filters-head">
          <button type="button" class="filters-reset-btn" id="studio-filter-clear" ${hasFilters ? '' : 'hidden'}>Azzera filtri</button>
        </div>
        <div class="filters-body studio-filters-body">
          <div class="filters-group">
            <span class="filters-group-label">Ruolo</span>
            <div class="role-filters filters-chip-track" role="group" aria-label="Filtra per ruolo Mantra">
              ${roleChips}
            </div>
          </div>
          <div class="filters-group">
            <span class="filters-group-label">Slot</span>
            <div class="filters-chip-track studio-slot-filters" role="group" aria-label="Filtra per slot">
              ${unslottedChip}
              ${slotChips}
            </div>
          </div>
          <div class="filters-group"${labelOptions.length ? '' : ' hidden'}>
            <span class="filters-group-label">Etichette</span>
            <div class="filters-chip-track tag-filters" role="group" aria-label="Filtra per etichetta">
              ${labelChips || '<span class="no-tags-message">Nessuna etichetta</span>'}
            </div>
          </div>
          <div class="filters-group"${teams.length ? '' : ' hidden'}>
            <span class="filters-group-label">Squadra</span>
            <div class="filters-chip-track" role="group" aria-label="Filtra per squadra">
              ${teamChips || '<span class="no-tags-message">Nessuna squadra</span>'}
            </div>
          </div>
        </div>
      </div>
    `;

    filtersEl.querySelector('.role-filters')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.role-filter');
      if (!btn) return;
      this.setStudioRoleFilter(btn.dataset.role);
    });
    filtersEl.querySelector('.studio-slot-filters')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.studio-slot-filter');
      if (!btn) return;
      this.setStudioBandFilter(btn.dataset.band);
    });
    filtersEl.querySelector('.tag-filters')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.tag-filter');
      if (!btn) return;
      this.toggleStudioLabelFilter(btn.dataset.label);
    });
    filtersEl.querySelector('[aria-label="Filtra per squadra"]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.studio-team-filter');
      if (!btn) return;
      this.setStudioTeamFilter(btn.dataset.team);
    });
    filtersEl.querySelector('#studio-filter-clear')?.addEventListener('click', () => this.clearStudioFilters());
  },

  _buildStudioCard(playerKey, target, player, band) {
    const gone = Boolean(this.isPlayerGoneInActiveAuction?.(playerKey));
    const inRoster = Boolean(this.state.roster?.has(playerKey));
    const exitedOnly = Boolean(
      !inRoster && this.state.auctionExits instanceof Map && this.state.auctionExits.has(playerKey)
    );
    const card = document.createElement('article');
    card.className = `studio-card studio-card--row studio-card--no-dnd${gone ? ' studio-card--sold' : ''}`;
    card.dataset.playerKey = playerKey;
    card.dataset.band = band;
    card.draggable = false;

    const name = player ? player.name : playerKey;
    const nameEsc = escapeHtml(name);
    const teamEsc = escapeHtml(player ? player.team || '' : '');
    const rawRoles = player
      ? (Array.isArray(player.roles) && player.roles.length > 0
          ? player.roles
          : String(player.role || '').split(';').map((r) => r.trim()).filter(Boolean))
      : [];
    const roleChipsHtml = `<div class="roles-container">${
      rawRoles.length
        ? rawRoles.map((r) => `<span class="${getRoleChipClass(r)}">${escapeHtml(r)}</span>`).join('')
        : '<span class="studio-role-more">—</span>'
    }</div>`;

    const maxPrice = Number(target.maxPrice) || 0;
    const priceText = formatPriceBandLabel(maxPrice, { empty: '—' });
    const labels = Array.isArray(target.labels) ? target.labels : [];
    const backups = Array.isArray(target.backups) ? target.backups : [];

    const main = document.createElement('div');
    main.className = 'studio-card-main';
    main.innerHTML = `
      ${roleChipsHtml}
      <div class="studio-card-id">
        <span class="studio-card-name">${nameEsc}</span>
        <span class="studio-card-team">${teamEsc}</span>
      </div>
    `;

    const metrics = document.createElement('div');
    metrics.className = 'studio-card-metrics';
    metrics.innerHTML = `
      <span class="studio-metric studio-metric--max" title="Prezzo max">
        <span class="studio-metric-lbl">max</span>
        <span class="studio-metric-val num">${priceText}</span>
      </span>
    `;
    if (gone) {
      const soldLabel = document.createElement('span');
      soldLabel.className = 'studio-sold-badge';
      soldLabel.textContent = inRoster ? 'Rosa' : 'Out';
      metrics.appendChild(soldLabel);
      if (exitedOnly) {
        const undoWrap = document.createElement('div');
        undoWrap.innerHTML = renderUndoExitButton({
          playerKey,
          name,
          team: player?.team || '',
          compact: true,
        });
        const undoBtn = undoWrap.firstElementChild;
        if (undoBtn) metrics.appendChild(undoBtn);
      }
    }

    const labelsEl = document.createElement('div');
    labelsEl.className = 'studio-card-labels';
    labelsEl.setAttribute('aria-label', 'Etichette');
    for (const l of labels) {
      const labelBtn = document.createElement('button');
      labelBtn.type = 'button';
      labelBtn.className = 'tag-filter studio-label-chip studio-label-chip--on active';
      labelBtn.title = `Filtra: ${l}`;
      labelBtn.textContent = l;
      applyLabelChipColor(labelBtn, this.getStudioLabelColor?.(l));
      labelBtn.addEventListener('click', () => this.toggleStudioLabelFilter(l));
      labelsEl.appendChild(labelBtn);
    }

    const reserves = this._buildStudioReservesEl(playerKey, backups);

    const actions = document.createElement('div');
    actions.className = 'studio-card-actions';

    const wrap = document.createElement('div');
    wrap.className = 'studio-list-actions studio-list-actions--quick';
    wrap.dataset.playerKey = playerKey;
    wrap.dataset.playerName = name;
    wrap.setAttribute('data-testid', 'studio-list-quick');
    this._appendStudioQuickActions(wrap, playerKey, target, { name, idScope: 'card' });
    actions.appendChild(wrap);

    const row = document.createElement('div');
    row.className = 'studio-card-row';
    row.appendChild(main);
    row.appendChild(reserves);
    row.appendChild(metrics);
    row.appendChild(labelsEl);
    row.appendChild(actions);
    card.appendChild(row);

    return card;
  },

  /**
   * Controlli rapidi target: prezzo | slot+note | tag | ⋯
   * Lista Svincolati: prezzo/note/tag → bozza persistente; slot → promuove a Studio.
   */
  _appendStudioQuickActions(host, playerKey, target, { name = '', idScope = 'card', readonly = false } = {}) {
    if (!host) return;
    if (readonly) {
      host.dataset.studioReadonly = 'true';
    }
    const seed = target || this.getStudioListSeed?.(playerKey) || {
      maxPrice: 0,
      band: null,
      labels: [],
      notes: '',
    };
    const maxPrice = Number(seed.maxPrice) || 0;
    const priceToggleText = formatPriceBandLabel(maxPrice, { empty: '€' });
    const inStudioNow = this.state.studioTargets.has(playerKey);
    const band = inStudioNow ? normalizeBand(seed.band) : null;
    const notesVal = String(seed.notes || '');
    const labels = Array.isArray(seed.labels) ? seed.labels : [];
    const idSafe = `${idScope}_${String(playerKey).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    const isInStudio = () => this.state.studioTargets.has(playerKey);
    const currentFields = () => {
      if (isInStudio()) return this.state.studioTargets.get(playerKey) || {};
      return this.getStudioDraft?.(playerKey) || { maxPrice: 0, notes: '', labels: [] };
    };

    const applyFields = async (fields, { soft = true } = {}) => {
      if (!isInStudio()) {
        if (fields.band !== undefined) {
          await this.promoteStudioDraft(playerKey, fields.band, fields);
          return;
        }
        this.setStudioDraftFields(playerKey, fields);
        return;
      }
      await this.setStudioTargetFields(playerKey, fields, { soft });
    };

    const pricePop = document.createElement('details');
    pricePop.className = 'studio-row-price';
    pricePop.dataset.playerKey = playerKey;
    pricePop.innerHTML = `
      <summary class="studio-row-price-toggle${maxPrice > 0 ? '' : ' is-empty'}" aria-label="${maxPrice > 0 ? `Prezzo max ${priceToggleText}` : 'Imposta prezzo max'}" title="Prezzo max">
        <span class="studio-row-price-val num">${priceToggleText}</span>
      </summary>
      <div class="studio-row-price-panel studio-price-bands" role="listbox" aria-label="Soglia prezzo max">
        ${renderPriceBandPickerHtml(maxPrice)}
      </div>
    `;
    let priceSaving = false;
    const paintPriceToggle = (nextMax) => {
      const next = Number(nextMax) || 0;
      const label = formatPriceBandLabel(next, { empty: '€' });
      const valEl = pricePop.querySelector('.studio-row-price-val');
      const toggle = pricePop.querySelector('.studio-row-price-toggle');
      if (valEl) valEl.textContent = label;
      if (toggle) {
        toggle.classList.toggle('is-empty', next <= 0);
        toggle.setAttribute(
          'aria-label',
          next > 0 ? `Prezzo max ${label}` : 'Imposta prezzo max'
        );
        toggle.title = next > 0 ? `Prezzo max ${label}` : 'Prezzo max';
      }
    };
    const selectPriceMax = async (nextMax) => {
      if (priceSaving) return;
      const next = Number(nextMax) || 0;
      const cur = currentFields();
      if ((Number(cur.maxPrice) || 0) === next) {
        pricePop.open = false;
        this._pinStudioPopoverPanel?.(pricePop);
        return;
      }
      paintPriceToggle(next);
      priceSaving = true;
      try {
        await applyFields({ maxPrice: next }, { soft: true });
      } finally {
        priceSaving = false;
        pricePop.open = false;
        this._pinStudioPopoverPanel?.(pricePop);
      }
    };
    pricePop.querySelector('.studio-row-price-panel')?.addEventListener('mousedown', (e) => {
      if (e.target.closest('.studio-price-chip')) e.preventDefault();
    });
    pricePop.querySelector('.studio-row-price-panel')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.studio-price-chip');
      if (!chip) return;
      e.preventDefault();
      e.stopPropagation();
      void selectPriceMax(chip.dataset.priceMax);
    });
    pricePop.addEventListener('toggle', () => {
      if (pricePop.open) {
        this._closeOtherStudioRowPopovers?.(pricePop);
        const curMax = Number(currentFields().maxPrice) || 0;
        const selected = findPriceBand(curMax);
        pricePop.querySelectorAll('.studio-price-chip').forEach((chip) => {
          const on = selected && chip.dataset.priceKey === selected.key;
          chip.classList.toggle('is-on', on);
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      this._pinStudioPopoverPanel?.(pricePop);
    });
    host.appendChild(pricePop);

    const metaPop = document.createElement('details');
    metaPop.className = 'studio-row-meta';
    metaPop.dataset.playerKey = playerKey;
    const slotBtns = BANDS.map(({ key }) => {
      const on = band !== null && band === key;
      return `<button type="button" class="studio-seg__btn${on ? ' is-on' : ''}" data-value="${key}" aria-pressed="${on ? 'true' : 'false'}">${key}</button>`;
    }).join('');
    metaPop.innerHTML = `
      <summary class="studio-row-meta-toggle${band ? '' : ' is-empty'}" aria-label="${band ? `Slot ${band} e note` : 'Scegli slot e note'}" title="Slot e note">
        <span class="studio-row-meta-slot">${band ? escapeHtml(band) : ICON_SLOT}</span>
        ${notesVal ? '<span class="studio-row-meta-dot" aria-hidden="true"></span>' : ''}
      </summary>
      <div class="studio-row-meta-panel">
        <span class="studio-form-label">Slot${band ? '' : ' (obbligatorio per Studio)'}</span>
        <div class="studio-seg studio-seg--slots" role="group" aria-label="Slot">
          ${slotBtns}
        </div>
        <label class="studio-form-label" for="studio-quick-notes-${idSafe}">Note</label>
        <input type="text" class="studio-form-control studio-quick-notes-input" id="studio-quick-notes-${idSafe}" value="${escapeHtml(notesVal)}" placeholder="Opzionale" maxlength="120" autocomplete="off">
      </div>
    `;
    const notesInput = metaPop.querySelector('.studio-quick-notes-input');
    let notesSaving = false;
    const slotGroup = metaPop.querySelector('.studio-seg');
    // Evita che il blur dell'input note chiuda il popover prima del click sullo slot
    slotGroup?.addEventListener('mousedown', (e) => {
      if (e.target.closest('.studio-seg__btn')) e.preventDefault();
    });
    slotGroup?.addEventListener('click', (e) => {
      const btn = e.target.closest('.studio-seg__btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const nextBand = btn.dataset.value;
      if (!nextBand) return;
      if (isInStudio() && nextBand === normalizeBand(this.state.studioTargets.get(playerKey)?.band)) {
        return;
      }
      metaPop.open = false;
      void applyFields({ band: nextBand }, { soft: false });
    });
    const commitNotes = async ({ close = true } = {}) => {
      if (notesSaving) return;
      const next = String(notesInput?.value || '').trim();
      const cur = currentFields();
      if (String(cur.notes || '') === next) return;
      notesSaving = true;
      try {
        await applyFields({ notes: next }, { soft: true });
      } finally {
        notesSaving = false;
        if (close) metaPop.open = false;
      }
    };
    notesInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void commitNotes({ close: true });
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        metaPop.open = false;
      }
    });
    notesInput?.addEventListener('blur', () => {
      if (metaPop.open) void commitNotes({ close: false });
    });
    metaPop.addEventListener('toggle', () => {
      if (metaPop.open) this._closeOtherStudioRowPopovers?.(metaPop);
      this._pinStudioPopoverPanel?.(metaPop);
    });
    host.appendChild(metaPop);

    const tagsPop = document.createElement('details');
    tagsPop.className = 'studio-row-tags';
    tagsPop.dataset.playerKey = playerKey;
    tagsPop.innerHTML = `
      <summary class="studio-row-tags-toggle${labels.length ? ' has-tags' : ''}" aria-label="Assegna tag" title="Tag">
        ${ICON_TAG}
      </summary>
      <div class="studio-row-tags-panel" role="group" aria-label="Tag del giocatore">
        <div class="studio-row-tags-chips">${this._renderStudioLabelChipsHtml(labels)}</div>
        <button type="button" class="studio-card-btn studio-card-btn--ghost studio-card-btn--compact studio-row-tags-manage">Gestisci tag</button>
      </div>
    `;
    tagsPop.querySelector('.studio-row-tags-chips')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.studio-label-chip');
      if (!chip) return;
      e.preventDefault();
      e.stopPropagation();
      void (async () => {
        const label = normalizeLabelName(chip.dataset.label);
        if (!label) return;
        const cur = currentFields();
        const nextLabels = [...(cur.labels || [])];
        const idx = nextLabels.indexOf(label);
        if (idx >= 0) nextLabels.splice(idx, 1);
        else {
          nextLabels.push(label);
          await this.ensureStudioUserLabelInCatalog(label);
        }
        const wasInStudioList = isInStudio() || Boolean(this.getStudioDraft?.(playerKey));
        await applyFields({ labels: nextLabels }, { soft: true });
        const stillInStudioList = isInStudio() || Boolean(this.getStudioDraft?.(playerKey));
        // Se togliendo il tag il giocatore esce da Studio, chiudi il pannello tag.
        if (wasInStudioList && !stillInStudioList) {
          tagsPop.open = false;
          this._pinStudioPopoverPanel?.(tagsPop);
          this._unpinAllStudioPopoverPanels?.();
        }
      })();
    });
    tagsPop.querySelector('.studio-row-tags-manage')?.addEventListener('click', (e) => {
      e.preventDefault();
      tagsPop.open = false;
      this.openStudioLabelsModal?.();
    });
    tagsPop.addEventListener('toggle', () => {
      if (tagsPop.open) this._closeOtherStudioRowPopovers?.(tagsPop);
      this._pinStudioPopoverPanel?.(tagsPop);
    });
    host.appendChild(tagsPop);

    const inCompare = () => {
      const slots = this.state.studioCompareSlots || [];
      return Boolean(this.state.studioCompareModalOpen && slots.includes(playerKey));
    };
    const compareBtn = document.createElement('button');
    compareBtn.type = 'button';
    compareBtn.className = `studio-row-compare-toggle${inCompare() ? ' is-on' : ''}`;
    compareBtn.setAttribute('aria-label', 'Confronta');
    compareBtn.setAttribute('aria-pressed', inCompare() ? 'true' : 'false');
    compareBtn.title = 'Confronta';
    compareBtn.innerHTML = ICON_COMPARE;
    compareBtn.addEventListener('click', () => {
      this._closeOtherStudioRowPopovers?.(null);
      this.addPlayerToStudioCompare(playerKey);
    });
    host.appendChild(compareBtn);

    const historyBtn = document.createElement('button');
    historyBtn.type = 'button';
    historyBtn.className = 'studio-row-history-toggle';
    historyBtn.setAttribute('aria-label', 'Storico');
    historyBtn.title = 'Storico';
    historyBtn.innerHTML = ICON_HISTORY;
    historyBtn.addEventListener('click', () => {
      this._closeOtherStudioRowPopovers?.(null);
      void this.openStudioHistoryModal(name);
    });
    host.appendChild(historyBtn);

    const hasRemove = !readonly && isInStudio();
    const hasClearDraft = !readonly && !hasRemove && Boolean(this.getStudioDraft?.(playerKey));
    if (hasRemove || hasClearDraft) {
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'studio-row-remove-toggle';
      const idleLabel = hasRemove ? 'Rimuovi dallo Studio' : 'Svuota bozza';
      const idleTitle = hasRemove ? 'Rimuovi' : 'Svuota bozza';
      removeBtn.setAttribute('aria-label', idleLabel);
      removeBtn.title = idleTitle;
      removeBtn.innerHTML = ICON_REMOVE;
      let armTimer = null;
      const disarm = () => {
        removeBtn.classList.remove('is-armed');
        removeBtn.setAttribute('aria-label', idleLabel);
        removeBtn.title = idleTitle;
        if (armTimer) {
          clearTimeout(armTimer);
          armTimer = null;
        }
      };
      removeBtn.addEventListener('blur', () => disarm());
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._closeOtherStudioRowPopovers?.(null);
        if (!removeBtn.classList.contains('is-armed')) {
          removeBtn.classList.add('is-armed');
          removeBtn.setAttribute('aria-label', 'Conferma rimozione');
          removeBtn.title = 'Clicca di nuovo per confermare';
          armTimer = setTimeout(disarm, 2200);
          return;
        }
        disarm();
        if (hasRemove) void this.removeFromStudio(playerKey);
        else {
          this.clearStudioDraft(playerKey);
          this.syncStudioButtonsInTable?.();
        }
      });
      host.appendChild(removeBtn);
    } else {
      const removeSlot = document.createElement('span');
      removeSlot.className = 'studio-row-remove-slot';
      removeSlot.setAttribute('aria-hidden', 'true');
      host.appendChild(removeSlot);
    }
  },

  _closeOtherStudioRowPopovers(exceptEl) {
    document
      .querySelectorAll(
        '.studio-row-price[open], .studio-row-meta[open], .studio-row-tags[open], .studio-row-more[open]'
      )
      .forEach((el) => {
        if (el === exceptEl) return;
        el.open = false;
        this._pinStudioPopoverPanel?.(el);
      });
  },

  _unpinAllStudioPopoverPanels() {
    document
      .querySelectorAll(
        'body > .studio-row-price-panel, body > .studio-row-meta-panel, body > .studio-row-tags-panel, body > .studio-row-more-panel'
      )
      .forEach((el) => el.remove());
  },

  /**
   * Nei contenitori tabella (overflow/contain) i panel absolute vengono tagliati.
   * In tabella/card lista: panel nel body, position:fixed ancorato al summary.
   */
  _pinStudioPopoverPanel(detailsEl) {
    if (!detailsEl) return;
    const panel =
      pinnedPopoverPanels.get(detailsEl) ||
      detailsEl.querySelector(STUDIO_POPOVER_PANEL_SEL);
    if (!panel) return;

    const clearPin = () => {
      if (pinnedPopoverPanels.get(detailsEl) === panel) {
        if (detailsEl.isConnected) detailsEl.appendChild(panel);
        else panel.remove();
        pinnedPopoverPanels.delete(detailsEl);
      }
      panel.style.position = '';
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.bottom = '';
      panel.style.width = '';
      panel.style.maxHeight = '';
      panel.style.zIndex = '';
      panel.style.visibility = '';
    };

    const inClippedHost = Boolean(detailsEl.closest(STUDIO_POPOVER_CLIP_HOST_SEL));
    if (!detailsEl.open || !inClippedHost) {
      clearPin();
      return;
    }

    requestAnimationFrame(() => {
      if (!detailsEl.open) {
        clearPin();
        return;
      }
      if (panel.parentElement !== document.body) {
        document.body.appendChild(panel);
        pinnedPopoverPanels.set(detailsEl, panel);
      }
      const anchor = detailsEl.querySelector('summary') || detailsEl;
      const rect = anchor.getBoundingClientRect();
      const isPrice = panel.classList.contains('studio-row-price-panel');
      const panelWidth = Math.min(isPrice ? 300 : 260, window.innerWidth - 16);
      const maxH = Math.min(340, window.innerHeight * 0.55);

      panel.style.position = 'fixed';
      panel.style.width = `${panelWidth}px`;
      panel.style.maxHeight = `${maxH}px`;
      panel.style.zIndex = '4000';
      panel.style.right = 'auto';
      panel.style.visibility = 'hidden';
      panel.style.left = '0px';
      panel.style.top = '0px';

      const h = Math.min(panel.scrollHeight || 200, maxH);
      let left = rect.right - panelWidth;
      if (left < 8) left = 8;
      if (left + panelWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - 8 - panelWidth);
      }
      let top = rect.bottom + 6;
      if (top + h > window.innerHeight - 8) {
        top = rect.top - 6 - h;
      }
      if (top < 8) {
        top = Math.max(8, window.innerHeight - 8 - h);
      }

      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.visibility = '';
    });
  },

  _syncStudioCardQuickFields(playerKey, updated) {
    const maxPrice = Number(updated?.maxPrice) || 0;
    const priceText = formatPriceBandLabel(maxPrice, { empty: '—' });
    const priceToggleText = formatPriceBandLabel(maxPrice, { empty: '€' });
    const hasBand = updated?.band != null && String(updated.band).trim() !== '';
    const band = hasBand ? normalizeBand(updated.band) : null;
    const notes = String(updated?.notes || '');
    const keySel = CSS.escape(playerKey);
    const selectedBand = findPriceBand(maxPrice);

    document.querySelectorAll(`.studio-card[data-player-key="${keySel}"]`).forEach((card) => {
      const metricVal = card.querySelector('.studio-metric--max .studio-metric-val');
      if (metricVal) metricVal.textContent = priceText;
    });

    document
      .querySelectorAll(
        `.studio-card[data-player-key="${keySel}"], .studio-list-actions--quick[data-player-key="${keySel}"]`
      )
      .forEach((root) => {
        const priceVal = root.querySelector('.studio-row-price-val');
        if (priceVal) priceVal.textContent = priceToggleText;
        const priceToggle = root.querySelector('.studio-row-price-toggle');
        if (priceToggle) {
          priceToggle.classList.toggle('is-empty', maxPrice <= 0);
          priceToggle.setAttribute(
            'aria-label',
            maxPrice > 0 ? `Prezzo max ${priceToggleText}` : 'Imposta prezzo max'
          );
        }
        root.querySelectorAll('.studio-price-chip').forEach((chip) => {
          const on = selectedBand && chip.dataset.priceKey === selectedBand.key;
          chip.classList.toggle('is-on', on);
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        const slotEl = root.querySelector('.studio-row-meta-slot');
        if (slotEl) {
          if (band) slotEl.textContent = band;
          else slotEl.innerHTML = ICON_SLOT;
        }
        const metaToggle = root.querySelector('.studio-row-meta-toggle');
        if (metaToggle) {
          metaToggle.classList.toggle('is-empty', !band);
          metaToggle.setAttribute('aria-label', band ? `Slot ${band} e note` : 'Scegli slot e note');
          let dot = metaToggle.querySelector('.studio-row-meta-dot');
          if (notes) {
            if (!dot) {
              dot = document.createElement('span');
              dot.className = 'studio-row-meta-dot';
              dot.setAttribute('aria-hidden', 'true');
              metaToggle.appendChild(dot);
            }
          } else if (dot) {
            dot.remove();
          }
        }

        root.querySelectorAll('.studio-row-meta .studio-seg__btn').forEach((btn) => {
          const on = band !== null && btn.dataset.value === band;
          btn.classList.toggle('is-on', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        const notesInput = root.querySelector('.studio-quick-notes-input');
        if (notesInput && document.activeElement !== notesInput) {
          notesInput.value = notes;
        }

        const moreBody = root.querySelector('.studio-row-more-body');
        if (moreBody) {
          let notesEl = moreBody.querySelector('.studio-card-notes');
          if (notes) {
            if (!notesEl) {
              notesEl = document.createElement('p');
              notesEl.className = 'studio-card-notes';
              moreBody.appendChild(notesEl);
            }
            notesEl.textContent = notes;
          } else if (notesEl) {
            notesEl.remove();
          }
        }
      });
  },

  _syncStudioRowTagsUi(playerKey, labels) {
    const keySel = CSS.escape(playerKey);
    const labelList = Array.isArray(labels) ? labels : [];
    document.querySelectorAll(`.studio-row-tags[data-player-key="${keySel}"]`).forEach((tagsPop) => {
      this._refreshStudioRowTagsContent(tagsPop, labelList);
    });
    document.querySelectorAll(`.studio-card[data-player-key="${keySel}"]`).forEach((card) => {
      this._syncStudioCardLabelsEl?.(card, labelList);
    });
  },

  _syncStudioCardLabelsEl(card, labels) {
    const labelsEl = card?.querySelector?.('.studio-card-labels');
    if (!labelsEl) return;
    labelsEl.replaceChildren();
    for (const l of labels || []) {
      const labelBtn = document.createElement('button');
      labelBtn.type = 'button';
      labelBtn.className = 'tag-filter studio-label-chip studio-label-chip--on active';
      labelBtn.title = `Filtra: ${l}`;
      labelBtn.textContent = l;
      applyLabelChipColor(labelBtn, this.getStudioLabelColor?.(l));
      labelBtn.addEventListener('click', () => this.toggleStudioLabelFilter(l));
      labelsEl.appendChild(labelBtn);
    }
  },

  _studioBackupCandidates(playerKey, backups, query) {
    const q = String(query || '').trim().toLowerCase();
    if (q.length < 1) return [];
    const taken = new Set((backups || []).map((b) => String(b).toLowerCase()));
    return (this.state.allPlayers || [])
      .filter((p) => {
        if (!p?.name) return false;
        if (playerToKey(p) === playerKey) return false;
        if (taken.has(String(p.name).toLowerCase())) return false;
        return String(p.name).toLowerCase().includes(q);
      })
      .slice(0, 8);
  },

  _bindStudioSuggestSearch(searchInput, suggestEl, { getHits, onPick, onEscape }) {
    if (!searchInput || !suggestEl) return;

    const clearSuggest = () => {
      suggestEl.hidden = true;
      suggestEl.innerHTML = '';
      searchInput.setAttribute('aria-expanded', 'false');
    };

    const renderSuggest = () => {
      const hits = getHits();
      if (!hits.length) {
        clearSuggest();
        return;
      }
      suggestEl.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
      suggestEl.innerHTML = hits.map((hit) => {
        const n = escapeHtml(hit.name);
        const team = escapeHtml(hit.team || '');
        return `<li role="option" class="studio-backup-suggest-item" data-value="${escapeHtml(hit.value)}" tabindex="-1"><span class="studio-backup-suggest-name">${n}</span><span class="studio-backup-suggest-team">${team}</span></li>`;
      }).join('');
    };

    const pickValue = (value) => {
      const clean = (value || '').trim();
      if (!clean) return;
      void onPick(clean);
    };

    searchInput.addEventListener('input', renderSuggest);
    searchInput.addEventListener('focus', renderSuggest);
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        clearSuggest();
        onEscape?.();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const first = suggestEl.querySelector('.studio-backup-suggest-item');
        if (first) pickValue(first.dataset.value);
      }
    });
    suggestEl.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const item = e.target.closest('.studio-backup-suggest-item');
      if (item) pickValue(item.dataset.value);
    });
  },

  _bindStudioBackupSearch(searchInput, suggestEl, playerKey) {
    if (!searchInput || !suggestEl) return;

    const currentBackups = () => {
      const t = this.state.studioTargets.get(playerKey);
      return Array.isArray(t?.backups) ? t.backups : [];
    };

    this._bindStudioSuggestSearch(searchInput, suggestEl, {
      getHits: () =>
        this._studioBackupCandidates(playerKey, currentBackups(), searchInput.value).map((p) => ({
          name: p.name,
          team: p.team,
          value: p.name,
        })),
      onPick: async (name) => {
        searchInput.value = '';
        suggestEl.hidden = true;
        suggestEl.innerHTML = '';
        searchInput.setAttribute('aria-expanded', 'false');
        const details = searchInput.closest('details.studio-reserve-add');
        if (details) details.open = false;
        await this.addStudioBackup(playerKey, name);
      },
    });
  },

  _buildStudioReservesEl(playerKey, backups) {
    const el = document.createElement('div');
    el.className = 'studio-card-reserves';
    el.setAttribute('aria-label', 'Riserve (max 2)');
    const list = (Array.isArray(backups) ? backups : []).slice(0, 2);

    for (let i = 0; i < 2; i += 1) {
      const slot = document.createElement('div');
      slot.className = 'studio-reserve-slot';
      const reserved = list[i];

      if (reserved) {
        slot.innerHTML = `
          <span class="studio-reserve-chip" title="Riserva ${i + 1}: ${escapeHtml(reserved)}">
            <span class="studio-meta-kind" aria-hidden="true">${i + 1}</span>
            <span class="studio-reserve-name">${escapeHtml(reserved)}</span>
            <button type="button" class="studio-backup-remove" data-name="${escapeHtml(reserved)}" aria-label="Rimuovi riserva ${escapeHtml(reserved)}">×</button>
          </span>
        `;
        slot.querySelector('.studio-backup-remove')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeStudioBackup(playerKey, reserved);
        });
      } else {
        const suggestId = `studio-reserve-suggest-${i}-${playerKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
        slot.innerHTML = `
          <details class="studio-reserve-add">
            <summary class="studio-reserve-add-btn" title="Aggiungi riserva ${i + 1}" aria-label="Aggiungi riserva ${i + 1}"><span class="studio-meta-kind" aria-hidden="true">${i + 1}</span>+</summary>
            <div class="studio-reserve-picker">
              <input type="search" class="studio-form-control studio-backup-search" placeholder="Cerca svincolato…" maxlength="40" aria-label="Cerca riserva ${i + 1} tra gli svincolati" autocomplete="off" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${suggestId}">
              <ul class="studio-backup-suggest" id="${suggestId}" role="listbox" hidden></ul>
            </div>
          </details>
        `;
        const details = slot.querySelector('details.studio-reserve-add');
        details?.addEventListener('toggle', () => {
          if (!details.open) return;
          document.querySelectorAll('details.studio-reserve-add[open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
          slot.querySelector('.studio-backup-search')?.focus();
        });
        this._bindStudioBackupSearch(
          slot.querySelector('.studio-backup-search'),
          slot.querySelector('.studio-backup-suggest'),
          playerKey,
        );
      }

      el.appendChild(slot);
    }

    return el;
  },

  _studioCompareCandidates(query, excludeKeys = new Set()) {
    const q = String(query || '').trim().toLowerCase();
    if (q.length < 1) return [];
    return (this.state.allPlayers || [])
      .filter((p) => {
        if (!p?.name) return false;
        const key = playerToKey(p);
        if (!key || excludeKeys.has(key)) return false;
        return String(p.name).toLowerCase().includes(q);
      })
      .slice(0, 8);
  },

  _bindStudioCompareSlot(slotEl, slotIndex) {
    if (!slotEl) return;
    const addBtn = slotEl.querySelector('.studio-compare-slot-add');
    const picker = slotEl.querySelector('.studio-compare-slot-picker');
    const searchInput = slotEl.querySelector('.studio-compare-slot-search');
    const suggestEl = slotEl.querySelector('.studio-compare-slot-suggest');
    const removeBtn = slotEl.querySelector('.studio-compare-slot-remove');

    const usedKeys = () => new Set(
      (this.state.studioCompareSlots || []).filter((k, i) => k && i !== slotIndex),
    );

    const closePicker = () => {
      if (!picker || !searchInput || !suggestEl) return;
      picker.hidden = true;
      searchInput.value = '';
      suggestEl.hidden = true;
      suggestEl.innerHTML = '';
      searchInput.setAttribute('aria-expanded', 'false');
      if (addBtn) addBtn.hidden = false;
    };

    const pickPlayer = (key) => {
      if (!key) return;
      closePicker();
      this.setStudioCompareSlot(slotIndex, key);
    };

    addBtn?.addEventListener('click', () => {
      if (!picker || !searchInput) return;
      picker.hidden = false;
      addBtn.hidden = true;
      searchInput.focus();
    });

    this._bindStudioSuggestSearch(searchInput, suggestEl, {
      getHits: () =>
        this._studioCompareCandidates(searchInput?.value, usedKeys()).map((p) => ({
          name: p.name,
          team: p.team,
          value: playerToKey(p),
        })),
      onPick: pickPlayer,
      onEscape: closePicker,
    });

    removeBtn?.addEventListener('click', () => {
      this.setStudioCompareSlot(slotIndex, null);
    });
  },

  _renderCompareSlotHeader(slotIndex, playerKey) {
    if (!playerKey) {
      return `
        <div class="studio-compare-slot studio-compare-slot--empty" data-slot="${slotIndex}">
          <button type="button" class="studio-compare-slot-add" aria-label="Aggiungi giocatore slot ${slotIndex + 1}">+</button>
          <div class="studio-compare-slot-picker" hidden>
            <input type="search" class="studio-form-control studio-compare-slot-search" placeholder="Cerca giocatore…" autocomplete="off" aria-label="Cerca giocatore per slot ${slotIndex + 1}" aria-expanded="false" aria-controls="studio-compare-suggest-${slotIndex}">
            <ul class="studio-backup-suggest studio-compare-slot-suggest" id="studio-compare-suggest-${slotIndex}" role="listbox" hidden></ul>
          </div>
        </div>`;
    }

    const player = this._resolveStudioPlayer(playerKey);
    const name = player ? player.name : playerKey;
    const team = player?.team || '';
    const rawRoles = player
      ? (Array.isArray(player.roles) && player.roles.length > 0
          ? player.roles
          : String(player.role || '').split(/[;,]/).map((r) => r.trim()).filter(Boolean))
      : [];
    const rolesHtml = rawRoles.length
      ? `<div class="roles-container studio-compare-player-roles">${
        rawRoles.map((r) => `<span class="${getRoleChipClass(r)}">${escapeHtml(r)}</span>`).join('')
      }</div>`
      : '';

    return `
      <div class="studio-compare-slot studio-compare-slot--filled" data-slot="${slotIndex}">
        <button type="button" class="studio-compare-slot-remove" aria-label="Rimuovi ${escapeHtml(name)}">×</button>
        <div class="studio-compare-player-head">
          <span class="studio-compare-player-logo" aria-hidden="true">
            ${buildTeamLogoHtml(team, { size: 28, className: 'team-logo team-logo--compare' })}
          </span>
          <div class="studio-compare-player-id">
            <span class="studio-compare-player-name">${escapeHtml(name)}</span>
            ${team ? `<span class="studio-compare-player-team">${escapeHtml(team)}</span>` : ''}
            ${rolesHtml}
          </div>
        </div>
      </div>`;
  },

  renderStudioCompare() {
    const el = document.getElementById('studio-compare');
    if (!el) return;
    const clearEscHandler = () => {
      if (this._studioCompareEscHandler) {
        document.removeEventListener('keydown', this._studioCompareEscHandler);
        this._studioCompareEscHandler = null;
      }
    };
    if (!this.state.studioCompareModalOpen) {
      clearEscHandler();
      el.hidden = true;
      el.classList.remove('is-open');
      el.innerHTML = '';
      return;
    }

    const slots = Array.isArray(this.state.studioCompareSlots)
      ? this.state.studioCompareSlots.slice(0, STUDIO_COMPARE_SLOT_COUNT)
      : Array(STUDIO_COMPARE_SLOT_COUNT).fill(null);
    while (slots.length < STUDIO_COMPARE_SLOT_COUNT) slots.push(null);

    const cols = slots.map((key, slotIndex) => ({
      slotIndex,
      key,
      player: key ? this._resolveStudioPlayer(key) : null,
      target: key ? (this.state.studioTargets.get(key) || {}) : {},
    }));
    const filledCount = cols.filter((c) => c.key).length;

    const formatValue = (v) => {
      if (v === null || v === undefined || v === '') return '—';
      return String(v);
    };

    const rows = [
      { label: 'FVM', get: (p) => formatFvmValue(p?.fvm), highlight: false },
      { label: 'Presenze', get: (p) => p?.matches, highlight: false },
      { label: 'Gol', get: (p) => p?.goals, highlight: false },
      { label: 'Assist', get: (p) => p?.assists, highlight: false },
      { label: 'Fantamedia', get: (p) => p?.fm_score, highlight: true },
      { label: 'Ammonizioni', get: (p) => p?.yellow_cards, highlight: false },
      { label: 'Espulsioni', get: (p) => p?.red_cards, highlight: false },
      { label: 'Prezzo max', get: (p, t) => t?.maxPrice, highlight: true },
    ];

    const head = `
      <tr>
        <th class="studio-compare-table-label-col" scope="col"></th>
        ${cols.map((c) => `<th class="studio-compare-player-col" scope="col">${this._renderCompareSlotHeader(c.slotIndex, c.key)}</th>`).join('')}
      </tr>`;
    const body = rows.map((r) => {
      const rowClass = r.highlight ? ' studio-compare-row--highlight' : '';
      return `<tr class="${rowClass.trim()}">
        <th scope="row">${escapeHtml(r.label)}</th>
        ${cols.map((c) => {
          if (!c.key) return '<td class="num studio-compare-cell--empty">—</td>';
          return `<td class="num">${escapeHtml(formatValue(r.get(c.player, c.target)))}</td>`;
        }).join('')}
      </tr>`;
    }).join('');

    const historySection = filledCount > 0
      ? `<section class="studio-compare-section studio-compare-history" aria-labelledby="studio-compare-history-title">
          <h4 class="studio-compare-section-title" id="studio-compare-history-title">Storico Fantamedia</h4>
          <p class="studio-history-loading">Caricamento storico…</p>
        </section>`
      : `<p class="studio-compare-empty-hint">Aggiungi almeno un giocatore con il tasto + per vedere statistiche e storico.</p>`;

    el.hidden = false;
    el.classList.add('is-open');
    el.innerHTML = `
      <div class="studio-compare-modal-panel" role="dialog" aria-modal="true" aria-labelledby="studio-compare-modal-title" data-testid="studio-compare-modal">
        <div class="studio-compare-modal-header">
          <h3 class="studio-compare-modal-title" id="studio-compare-modal-title">Confronto giocatori</h3>
          <button type="button" class="studio-compare-modal-close" aria-label="Chiudi confronto">×</button>
        </div>
        <div class="studio-compare-modal-meta">
          <span class="studio-compare-meta-pill">${filledCount} di ${STUDIO_COMPARE_SLOT_COUNT} giocatori</span>
        </div>
        <div class="studio-compare-modal-body">
          <section class="studio-compare-section" aria-labelledby="studio-compare-stats-title">
            <h4 class="studio-compare-section-title" id="studio-compare-stats-title">Statistiche stagione</h4>
            <div class="studio-compare-table-scroll">
              <table class="studio-compare-table">
                <colgroup><col class="studio-compare-table-label-col"><col span="${STUDIO_COMPARE_SLOT_COUNT}" class="studio-compare-player-col"></colgroup>
                <thead>${head}</thead>
                <tbody>${body}</tbody>
              </table>
            </div>
          </section>
          ${historySection}
        </div>
      </div>
    `;

    cols.forEach((c) => {
      const slotEl = el.querySelector(`.studio-compare-slot[data-slot="${c.slotIndex}"]`);
      this._bindStudioCompareSlot(slotEl, c.slotIndex);
    });

    const histWrap = el.querySelector('.studio-compare-history');
    if (histWrap && filledCount > 0) {
      void (async () => {
        const entries = await Promise.all(
          cols.map(async (c) => {
            if (!c.key) return null;
            const playerName = c.player ? c.player.name : c.key;
            const seasons = await getPlayerHistory(playerName);
            return { name: playerName, seasons };
          }),
        );
        const historyHtml = buildHistoryCompareTableHTML(entries);
        histWrap.innerHTML = `
          <h4 class="studio-compare-section-title" id="studio-compare-history-title">Storico Fantamedia</h4>
          ${historyHtml}`;
      })();
    }

    const close = () => {
      this.closeStudioCompare();
    };
    el.querySelector('.studio-compare-modal-close')?.addEventListener('click', close);
    el.onclick = (e) => {
      if (e.target === el) close();
    };
    if (this._studioCompareEscHandler) {
      document.removeEventListener('keydown', this._studioCompareEscHandler);
    }
    this._studioCompareEscHandler = (e) => {
      if (e.key === 'Escape' && this.state.studioCompareModalOpen) close();
    };
    document.addEventListener('keydown', this._studioCompareEscHandler);
  },

  openStudioLabelsModal() {
    document.getElementById('studio-labels-modal')?.remove();
    const custom = [...(this.getStudioLabelOptions?.([]).custom || this.state.studioUserLabels || [])];

    const overlay = document.createElement('div');
    overlay.id = 'studio-labels-modal';
    overlay.className = 'studio-compare is-open';
    overlay.setAttribute('data-testid', 'studio-labels-modal');

    const renderList = () => {
      const list = overlay.querySelector('#studio-labels-modal-list');
      if (!list) return;
      if (!custom.length) {
        list.innerHTML = `
          <li class="studio-labels-modal-empty" aria-live="polite">
            <span class="studio-labels-modal-empty-icon" aria-hidden="true">${ICON_TAG}</span>
            <p class="studio-labels-modal-empty-text">Nessun tag ancora. Creane uno qui sopra.</p>
          </li>`;
        return;
      }
      list.innerHTML = custom
        .map((name) => {
          const used = this.countTargetsWithLabel?.(name) || 0;
          const color = this.getStudioLabelColor?.(name) || DEFAULT_STUDIO_LABEL_COLOR;
          return `
            <li class="studio-labels-modal-row" data-label="${escapeHtml(name)}">
              <div class="studio-labels-modal-row-main">
                <input type="text" class="studio-labels-modal-name" value="${escapeHtml(name)}" maxlength="24" aria-label="Rinomina ${escapeHtml(name)}">
                <span class="studio-labels-modal-used-pill" title="Target con questo tag">
                  <span class="studio-labels-modal-used-num">${used}</span>
                  <span class="studio-labels-modal-used-label">target</span>
                </span>
              </div>
              <div class="studio-labels-modal-row-actions">
                <button type="button" class="studio-labels-modal-action studio-labels-rename-btn" aria-label="Salva nome" title="Salva">${ICON_SAVE_CHECK}</button>
                <button type="button" class="studio-labels-modal-action studio-labels-modal-action--danger studio-labels-delete-btn" aria-label="Elimina tag" title="Elimina">${ICON_REMOVE}</button>
              </div>
              <div class="studio-labels-modal-row-color">
                ${renderLabelColorPickerHtml(color, { compact: true })}
              </div>
            </li>`;
        })
        .join('');
    };

    overlay.innerHTML = `
      <div class="studio-compare-modal-panel studio-labels-modal-panel" role="dialog" aria-modal="true" aria-labelledby="studio-labels-modal-title">
        <div class="studio-compare-modal-header">
          <h3 class="studio-compare-modal-title" id="studio-labels-modal-title">Gestisci tag</h3>
          <button type="button" class="studio-compare-modal-close" aria-label="Chiudi">×</button>
        </div>
        <div class="studio-compare-modal-body studio-labels-modal-body">
          <p class="studio-labels-modal-hint">Crea, rinomina, colora o elimina i tag usati nei target Studio.</p>
          <div class="studio-labels-modal-add">
            <label class="studio-labels-modal-add-label" for="studio-labels-modal-input">Nuovo tag</label>
            <div class="studio-labels-modal-add-row">
              <input type="text" class="studio-labels-modal-input" id="studio-labels-modal-input" placeholder="Es. da evitare, titolare…" maxlength="24" autocomplete="off">
              <button type="button" class="studio-labels-modal-add-btn" id="studio-labels-modal-add">Aggiungi</button>
            </div>
            <div class="studio-labels-modal-add-color" id="studio-labels-modal-add-color">
              <span class="studio-labels-modal-color-label">Colore</span>
              ${renderLabelColorPickerHtml(DEFAULT_STUDIO_LABEL_COLOR)}
            </div>
          </div>
          <div class="studio-labels-modal-section">
            <h4 class="studio-compare-section-title">I tuoi tag</h4>
            <ul class="studio-labels-modal-list" id="studio-labels-modal-list"></ul>
          </div>
        </div>
      </div>`;

    const close = () => {
      if (this._studioLabelsEscHandler) {
        document.removeEventListener('keydown', this._studioLabelsEscHandler);
        this._studioLabelsEscHandler = null;
      }
      overlay.remove();
      this.refreshStudioLabelCatalogUi?.({ rebuildQuickActions: true });
    };

    const addLabel = async () => {
      const input = overlay.querySelector('#studio-labels-modal-input');
      const val = String(input?.value || '').trim();
      if (!val) return;
      const colorInput = overlay.querySelector('#studio-labels-modal-add-color .studio-label-color-value');
      const color = colorInput?.value || DEFAULT_STUDIO_LABEL_COLOR;
      try {
        const name = await this.createStudioUserLabel(val, color);
        if (name && !custom.includes(name)) {
          custom.push(name);
          custom.sort((a, b) => a.localeCompare(b, 'it'));
        }
        input.value = '';
        syncLabelColorPickerSelection(
          overlay.querySelector('#studio-labels-modal-add-color .studio-label-color-picker'),
          DEFAULT_STUDIO_LABEL_COLOR,
        );
        renderList();
        this.refreshStudioLabelCatalogUi?.();
      } catch (err) {
        console.warn('[Studio] add label modal:', err);
      }
    };

    overlay.querySelector('.studio-compare-modal-close')?.addEventListener('click', close);
    overlay.onclick = (e) => {
      if (e.target === overlay) close();
    };
    this._studioLabelsEscHandler = (e) => {
      if (e.key !== 'Escape') return;
      if (!document.getElementById('studio-labels-modal')) return;
      const dialogOverlay = document.getElementById('custom-dialog-overlay');
      if (dialogOverlay?.style.display === 'flex') return;
      e.preventDefault();
      close();
    };
    document.addEventListener('keydown', this._studioLabelsEscHandler);

    overlay.querySelector('#studio-labels-modal-add')?.addEventListener('click', () => {
      void addLabel();
    });
    overlay.querySelector('#studio-labels-modal-input')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void addLabel();
      }
    });

    overlay.addEventListener('click', async (e) => {
      const colorChip = e.target.closest('.studio-label-color-chip');
      if (colorChip) {
        e.preventDefault();
        e.stopPropagation();
        const picker = colorChip.closest('.studio-label-color-picker');
        const color = colorChip.dataset.color;
        const row = colorChip.closest('.studio-labels-modal-row');
        if (!row) {
          syncLabelColorPickerSelection(picker, color);
          return;
        }
        const oldName = row.getAttribute('data-label') || row.dataset.label || '';
        if (!oldName) return;
        if (normalizeLabelColor(this.getStudioLabelColor?.(oldName)) === normalizeLabelColor(color)) {
          syncLabelColorPickerSelection(picker, color);
          return;
        }
        try {
          await this.updateStudioUserLabelColor(oldName, color, { render: false });
          syncLabelColorPickerSelection(picker, color);
          this.refreshStudioLabelCatalogUi?.();
        } catch (err) {
          console.warn('[Studio] update label color:', err);
          syncLabelColorPickerSelection(picker, this.getStudioLabelColor?.(oldName));
          window.alert('Colore non salvato. Riprova.');
        }
        return;
      }

      const row = e.target.closest('.studio-labels-modal-row');
      if (!row) return;
      const oldName = row.dataset.label;
      if (e.target.closest('.studio-labels-delete-btn')) {
        const used = this.countTargetsWithLabel?.(oldName) || 0;
        const ok = used > 0
          ? await window.showConfirm(
            'Elimina tag',
            `Eliminare «${oldName}»? Verrà rimosso da ${used} target.`,
            'Elimina',
            'Annulla',
            'danger',
          )
          : await window.showConfirm(
            'Elimina tag',
            `Eliminare «${oldName}»?`,
            'Elimina',
            'Annulla',
            'danger',
          );
        if (!ok) return;
        try {
          await this.deleteStudioUserLabel(oldName);
          const idx = custom.indexOf(oldName);
          if (idx >= 0) custom.splice(idx, 1);
          renderList();
        } catch (err) {
          console.warn('[Studio] delete label:', err);
        }
        return;
      }
      if (e.target.closest('.studio-labels-rename-btn')) {
        const input = row.querySelector('.studio-labels-modal-name');
        const newName = String(input?.value || '').trim();
        if (!newName || newName === oldName) return;
        try {
          await this.renameStudioUserLabel(oldName, newName);
          custom.splice(0, custom.length, ...(this.state.studioUserLabels || []));
          renderList();
        } catch (err) {
          console.warn('[Studio] rename label:', err);
          window.alert(err?.message || 'Rinomina non riuscita');
        }
      }
    });

    document.body.appendChild(overlay);
    renderList();
    overlay.querySelector('#studio-labels-modal-input')?.focus();
  },

  async openStudioHistoryModal(playerName) {
    const title = String(playerName || '').trim() || 'Giocatore';
    document.getElementById('studio-history-modal')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'studio-history-modal';
    overlay.className = 'studio-compare is-open studio-history-modal';
    overlay.innerHTML = `
      <div class="studio-compare-modal-panel studio-history-modal-panel" role="dialog" aria-modal="true" aria-labelledby="studio-history-modal-title">
        <div class="studio-compare-modal-header">
          <h3 class="studio-compare-modal-title" id="studio-history-modal-title">Storico — ${escapeHtml(title)}</h3>
          <button type="button" class="studio-compare-modal-close" aria-label="Chiudi storico">×</button>
        </div>
        <div class="studio-compare-modal-body studio-history-modal-body">
          <p class="studio-history-loading">Caricamento…</p>
        </div>
      </div>
    `;

    const close = () => {
      if (this._studioHistoryEscHandler) {
        document.removeEventListener('keydown', this._studioHistoryEscHandler);
        this._studioHistoryEscHandler = null;
      }
      overlay.remove();
    };

    overlay.querySelector('.studio-compare-modal-close')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    this._studioHistoryEscHandler = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', this._studioHistoryEscHandler);
    document.body.appendChild(overlay);

    const body = overlay.querySelector('.studio-history-modal-body');
    try {
      const seasons = await getPlayerHistory(title);
      if (!overlay.isConnected || !body) return;
      body.innerHTML = buildHistoryTableHTML(seasons);
    } catch {
      if (!overlay.isConnected || !body) return;
      body.innerHTML = '<p class="studio-history-empty">Impossibile caricare lo storico</p>';
    }
  },

  showStudioReconcileModal(result) {
    const changed = result.changedTeam || [];
    const missing = result.missing || [];
    if (!changed.length && !missing.length) return;

    const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    const overlay = document.createElement('div');
    overlay.className = 'studio-compare is-open';
    const close = () => overlay.remove();

    const changedRows = changed
      .map(
        (c) => `
        <li class="studio-reconcile-row" data-old="${c.oldKey}" data-new="${c.newKey}">
          <span class="studio-reconcile-name">${esc(c.name)}</span>
          <span class="studio-reconcile-move">${esc(c.oldTeam)} → ${esc(c.newTeam)}</span>
          <button class="studio-reconcile-relink" data-old="${c.oldKey}" data-new="${c.newKey}">È lui? Ricollega</button>
          <button class="studio-reconcile-remove" data-key="${c.oldKey}">Togli</button>
        </li>`,
      )
      .join('');

    const missingRows = missing
      .map((key) => {
        const name = key.split('|')[0];
        const team = key.split('|')[1] || '';
        return `
        <li class="studio-reconcile-row" data-key="${key}">
          <span class="studio-reconcile-name">${esc(name)}</span>
          <span class="studio-reconcile-move">${esc(team)} — non in lista</span>
          <button class="studio-reconcile-keep" data-key="${key}">Tieni da parte</button>
          <button class="studio-reconcile-remove" data-key="${key}">Togli dallo Studio</button>
        </li>`;
      })
      .join('');

    overlay.innerHTML = `
      <div class="studio-compare-modal-panel" role="dialog" aria-label="Giocatori da sistemare">
        <div class="studio-compare-modal-header">
          <h3 class="studio-compare-modal-title">Giocatori da sistemare</h3>
          <button class="studio-compare-modal-close" aria-label="Chiudi">×</button>
        </div>
        <div class="studio-compare-modal-body">
          ${changed.length ? `<h4>Hanno cambiato squadra</h4><ul class="studio-reconcile-list">${changedRows}</ul>` : ''}
          ${missing.length ? `<h4>Non più in lista</h4><ul class="studio-reconcile-list">${missingRows}</ul>` : ''}
        </div>
        <div class="studio-compare-modal-footer">
          <button class="studio-reconcile-later">Sistemo dopo</button>
        </div>
      </div>`;

    overlay.querySelector('.studio-compare-modal-close')?.addEventListener('click', close);
    overlay.querySelector('.studio-reconcile-later')?.addEventListener('click', close);
    overlay.onclick = (e) => { if (e.target === overlay) close(); };

    overlay.querySelectorAll('.studio-reconcile-relink').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await this.relinkStudioTarget(btn.dataset.old, btn.dataset.new);
        btn.closest('.studio-reconcile-row')?.remove();
      });
    });
    overlay.querySelectorAll('.studio-reconcile-keep').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await this.keepMissingTarget(btn.dataset.key);
        btn.closest('.studio-reconcile-row')?.remove();
      });
    });
    overlay.querySelectorAll('.studio-reconcile-remove').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await this.removeFromStudio(btn.dataset.key);
        btn.closest('.studio-reconcile-row')?.remove();
      });
    });

    document.body.appendChild(overlay);
  },
};
